import { inspectMessage } from "@chatbotgang/etude/debug/inspectMessage";
import { css } from "@emotion/react";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import { createQueriesContext } from "@polifonia/utils/react-query/createQueriesContext";
import { getRouteApi, Link } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";

import { BarLoading } from "@/components/Loading/BarLoading";
import { Typography } from "@/components/Typography";
import { interlude } from "@/interlude";
import { ChannelIcon } from "@/pages/channels/components/ChannelIcon";
import { ChannelNameCard } from "@/pages/channels/components/ChannelNameCard";
import { PageTitle } from "@/pages/components/PageTitle";

const route = getRouteApi("/channels/$type/$id");

const useChannelQueries = (channelUuid: string) => {
  const channelQuery = interlude.line.useSuspenseGetByUuid({
    params: {
      channelUuid,
    },
  });
  return {
    channel: channelQuery,
  };
};

const ChannelQueriesContext =
  createQueriesContext<ReturnType<typeof useChannelQueries>>();

const ChannelQueriesContextProvider: React.FC<{
  channelUuid: string;
  children: React.ReactNode;
}> = ({ channelUuid, children }) => {
  const channelQueries = useChannelQueries(channelUuid);
  return (
    <ChannelQueriesContext.Provider queries={channelQueries}>
      {children}
    </ChannelQueriesContext.Provider>
  );
};

const ChannelNameEdit: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const channelData = ChannelQueriesContext.useData();
  const updateMutation = interlude.line.useUpdateChannel(
    {
      params: {
        channelUuid: channelData.channel.uuid,
      },
    },
    {
      onSuccess() {
        enqueueSnackbar(t("common.updatedSuccessfully"), {
          variant: "success",
        });
      },
      onError() {
        enqueueSnackbar(t("common.apiError.unexpectedError"), {
          variant: "error",
        });
      },
    },
  );

  return (
    <ChannelNameCard
      value={channelData.channel.name}
      onSubmit={async (name) => {
        await updateMutation.mutateAsync({ name });
      }}
      disabled={updateMutation.isPending}
      icon={<ChannelIcon type={channelData.channel.type} />}
      description={
        <Typography variant="note">
          {t("page.channel.config.externalId.line.label", {
            channelId: channelData.channel.externalChannelId,
          })}
        </Typography>
      }
    />
  );
};

const LineDetailPageInternal: React.FC = () => {
  const { t } = useTranslation();
  const channelData = ChannelQueriesContext.useData();
  return (
    <Stack gap="8px">
      <Breadcrumbs aria-label="breadcrumb" separator=">">
        <Link to="/channels" css={css({ textDecoration: "none" })}>
          <Typography variant="body" color={theme.colors.staticFgBody}>
            {t("channels.title")}
          </Typography>
        </Link>
        <Link
          to="/channels/$type/$id"
          params={{
            type: "line",
            id: channelData.channel.uuid,
          }}
          css={css({ textDecoration: "none" })}
        >
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {t("common.line")}
          </Typography>
        </Link>
      </Breadcrumbs>
      <PageTitle>{t("page.channel.line.detail.title")}</PageTitle>
      <Stack gap="24px" css={css({ maxWidth: "512px" })}>
        <Stack gap="8px">
          <Typography
            variant="h3"
            fontWeight="medium"
            color={theme.colors.staticFgTitle}
          >
            {t("page.channel.line.detail.name")}
          </Typography>
          <ChannelNameEdit />
        </Stack>
      </Stack>
    </Stack>
  );
};

export const LineDetailPage: React.FC = () => {
  const { id } = route.useParams();

  if (typeof id !== "string") {
    throw new Error(inspectMessage`Unexpected channel id: ${id}`);
  }

  return (
    <Suspense fallback={<BarLoading />}>
      <ChannelQueriesContextProvider channelUuid={id}>
        <LineDetailPageInternal />
      </ChannelQueriesContextProvider>
    </Suspense>
  );
};
